import { ShieldCheck, ShieldAlert } from "lucide-react";
import type { SecurityOverview } from "@/hooks/use-access";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  data?: SecurityOverview;
  isLoading: boolean;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function SecurityPanel({ data, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  const failedByIp = data?.failed_by_ip ?? [];
  const failedLogins = data?.failed_logins ?? [];
  const denied = data?.denied_access ?? [];

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-1 text-sm font-semibold text-gray-900">
          Intentos fallidos por IP
        </h3>
        <p className="mb-3 text-xs text-gray-500">
          Varios usuarios distintos probados desde una misma IP es la señal
          típica de un ataque por fuerza bruta.
        </p>

        {failedByIp.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck />}
            title="Ningún intento fallido"
            description="No se han registrado inicios de sesión fallidos en el periodo."
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">IP</th>
                  <th className="px-4 py-3 text-right font-medium">Intentos</th>
                  <th className="px-4 py-3 text-left font-medium">Usuarios probados</th>
                  <th className="px-4 py-3 text-left font-medium">Último</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {failedByIp.map((row) => (
                  <tr key={row.ip} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">{row.ip}</td>
                    <td className="px-4 py-2 text-right">
                      <span
                        className={`font-semibold ${
                          row.attempts >= 5 ? "text-red-600" : "text-gray-700"
                        }`}
                      >
                        {row.attempts}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {row.usernames?.join(", ") || "—"}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {formatDate(row.last_attempt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">
          Últimos inicios de sesión fallidos
        </h3>
        {failedLogins.length === 0 ? (
          <p className="text-sm text-gray-500">Sin intentos fallidos recientes.</p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
            {failedLogins.map((attempt, index) => (
              <li
                key={`${attempt.created_at}-${index}`}
                className="flex items-center justify-between px-4 py-2 text-sm"
              >
                <span className="text-gray-900">{attempt.actor_label || "—"}</span>
                <span className="font-mono text-xs text-gray-500">{attempt.ip || "—"}</span>
                <span className="text-xs text-gray-500">
                  {formatDate(attempt.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Accesos denegados</h3>
        {denied.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck />}
            title="Sin accesos denegados"
            description="Nadie ha intentado entrar donde no debía."
          />
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
            {denied.map((item) => (
              <li key={item.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                <ShieldAlert className="h-4 w-4 flex-shrink-0 text-amber-500" />
                <span className="text-gray-900">{item.actor_label || "desconocido"}</span>
                <span className="flex-1 truncate font-mono text-xs text-gray-500">
                  {item.method} {item.path}
                </span>
                <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
